import { type ComponentProps, Fragment } from 'react'

import { ButtonDefault } from '@/components/common/Button'
import { ReadMoreLink } from '@/components/common/ReadMoreLink'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { type ComboBracketType, type ComboPreviewType } from '@/defs/combo'
import { FILTER_BRACKET_LABELS, FILTER_BRACKETS } from '@/defs/filters'
import { type IconType } from '@/defs/icon'
import { useLinkInternal } from '@/hooks/useLinkInternal'
import { cn } from '@/utils/commonUtil'

import { StatCommonBox } from '../../StatCommon/StatCommonBox/StatCommonBox'

import styles from './StatComboBrackets.module.scss'

type Props = {
  combo: ComboPreviewType
}

const CONFIG_ICON: Record<ComboBracketType['status'], [ComponentProps<typeof ButtonDefault>['colorScheme'], IconType]> =
  {
    invalid: ['warning', 'xMark'],
    valid: ['success', 'check'],
    warning: ['alert', 'exclamation'],
  }

const getBracketInfo = ({ combo }: Props) =>
  FILTER_BRACKETS.map((bracket) => {
    const { message, status } = ['exhibition', 'core'].includes(bracket)
      ? { ...combo.bracketInfo.low }
      : ['upgraded'].includes(bracket)
        ? { ...combo.bracketInfo.mid }
        : {
            message: 'All combos are allowed.',
            status: 'valid' as const,
          }
    const [colorScheme, icon] = CONFIG_ICON[status]
    const { label, labelLong } = FILTER_BRACKET_LABELS[bracket]

    return { bracket, colorScheme, icon, label, labelLong, message, status }
  })

export const StatComboBrackets = ({ combo }: Props) => {
  const { buildRelative } = useLinkInternal()
  const bracketInfo = getBracketInfo({ combo })

  const route = buildRelative({
    colorId: combo.colorId,
    comboSlug: combo.id,
    section: 'combosFeatured',
  })
  const href = route ? `${route}#bracket` : undefined

  const popover = (
    <>
      {bracketInfo.map(({ colorScheme, icon, label, labelLong, message }, i) => (
        <Fragment key={i}>
          <p className={cn(styles.heading, { colorScheme, styles })}>
            <IconWithLabel iconBefore={icon}>
              Bracket {label}: {labelLong}
            </IconWithLabel>
          </p>
          <p>{message}</p>
        </Fragment>
      ))}
      {href && (
        <p>
          <ReadMoreLink as='LinkRoute' href={href} />
        </p>
      )}
    </>
  )

  return (
    <StatCommonBox label='Brackets' popover={popover}>
      <ul className={styles.container}>
        {bracketInfo.map(({ bracket, colorScheme, icon, label }) => (
          <li key={bracket}>
            <ButtonDefault as='div' boxshadow='none' colorScheme={colorScheme} hover='none' size='sm'>
              <IconWithLabel iconBefore={icon} iconBeforeSize='sm'>
                {label}
              </IconWithLabel>
            </ButtonDefault>
          </li>
        ))}
      </ul>
    </StatCommonBox>
  )
}
